import { ref } from 'vue';

/**
 * Toast notifications.
 *
 * A module-level store rather than a provide/inject service, so any code — including
 * an API error interceptor with no component context — can raise one.
 */

export type ToastTone = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

export const toasts = ref<Toast[]>([]);

let nextId = 1;

/** Errors stay until dismissed; everything else clears itself. */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 5000,
  warning: 7000,
  error: 0,
};

export function toast(tone: ToastTone, title: string, description?: string): number {
  const id = nextId++;
  toasts.value = [...toasts.value, { id, tone, title, ...(description ? { description } : {}) }];

  const duration = DEFAULT_DURATION[tone];
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  // Cap the stack: an unattended tab that accumulates fifty toasts hides the page.
  if (toasts.value.length > 4) {
    toasts.value = toasts.value.slice(-4);
  }
  return id;
}

export function dismissToast(id: number): void {
  toasts.value = toasts.value.filter((entry) => entry.id !== id);
}

export const notify = {
  success: (title: string, description?: string) => toast('success', title, description),
  error: (title: string, description?: string) => toast('error', title, description),
  warning: (title: string, description?: string) => toast('warning', title, description),
  info: (title: string, description?: string) => toast('info', title, description),
};
